import React from 'react'
import { motion } from 'framer-motion'

import Heading from '../../atoms/Heading/Heading'

import outcomebg from '../../../app/assets/images/homepage/outcomebg.webp'

interface MeasurableOutcomesProps {
  data?: {
    title?: string
    subTitle?: string
    text?: string
  }
}

const outcomes = [
  {
    number: '7+',
    text: `Reduction in manual ops hours
for typical automation clients`,
    color: '#229CFF',

    position: 'md:-translate-y-14 xl:-translate-y-20',

    spacing: 'md:pt-0 xl:pt-0',
  },

  {
    number: '3–6 wks',
    text: `Average time-to-production
for agentic workflow deployments`,
    color: '#229CFF',

    position: 'md:translate-y-12 xl:translate-y-20',

    spacing: 'md:pt-10 xl:pt-16',
  },

  {
    number: '100%',
    text: `In-house delivery — your IP, your
code, your infrastructure`,
    color: '#229CFF',

    position: 'md:-translate-y-14 xl:-translate-y-20',

    spacing: 'md:pt-0 xl:pt-0',
  },
]

const MeasurableOutcomes = ({
  data,
}: MeasurableOutcomesProps) => {
  return (
    <>

      {/* OUTCOME SECTION */}
      <section className="relative overflow-hidden bg-[#0B0B0B] pb-12 md:py-16 xl:py-20">

        {/* BG IMAGE */}
        <img
          src={outcomebg}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-100"
        />



        {/* CONTENT */}
        <div className="relative z-10 mb-25">

          {/* HEADING */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: false }}
            className="mx-auto w-full max-w-[1200px] px-5 text-center sm:px-8 md:px-12 lg:px-16"
          >
            <Heading
              title={data?.title}
              subTitle={data?.subTitle}
              text={data?.text}
              className="!px-0"
            />
          </motion.div>

          {/* GRID */}
          <div className="mx-auto mt-10 grid w-[90%] max-w-[1500px] grid-cols-1 gap-10 md:grid-cols-3 md:gap-4 xl:mt-20">

            {outcomes.map((item, index) => (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 80,
                  scale: 0.8,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  ease: 'easeOut',
                }}
                viewport={{ once: false }}
                className={`flex flex-col items-center text-center ${item.position} ${item.spacing}`}
              >

                {/* NUMBER */}
                <h2
                  className="font-google-sans-bold text-3xl md:text-4xl xl:text-5xl"
                  style={{
                    color: item.color,
                  }}
                >
                  {item.number}
                </h2>

                {/* TEXT */}
                <p className="mt-3 md:mt-5 whitespace-pre-line font-google-sans text-base leading-[1.7] text-white/90 md:text-lg xl:text-xl">
                  {item.text}
                </p>

              </motion.div>
            ))}

          </div>

        </div>

      </section>

      {/* TESTIMONIAL SECTION */}
      <section className="relative overflow-hidden bg-[#229CFF]/20">

        {/* BLUE GLOW */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[150px]"
          style={{
            background: '#229CFF',
            opacity: 0.12,
          }}
        />


        {/* CONTENT */}
        <motion.div
          initial={{
            opacity: 0,
            y: 80,
            scale: 0.9,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          transition={{
            duration: 0.8,
            ease: 'easeOut',
          }}
          viewport={{ once: false }}
          className="relative z-20 mx-auto max-w-[1500px] px-4 py-4 text-center md:px-10 md:py-14 xl:px-20"
        >

          {/* QUOTE */}
          <p className="font-google-sans-medium text-base md:leading-[1.5] lg:leading-[2.4] text-white md:text-2xl xl:text-3xl">
            “ The Sattva Tech{' '}

            <span
              style={{
                background:
                  'linear-gradient(118.8deg, #ED943C 27.32%, #E73636 91.18%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              didn't just automate our workflow — they architected
              the entire data pipeline from scratch. What used to
              take our team three days now runs overnight without
              anyone touching it.
            </span>

            ”
          </p>

          {/* BOTTOM TEXT */}
          <p className="mt-4 md:mt-8 font-google-sans text-sm text-white/90 md:text-lg xl:text-2xl">
            — Client,{' '}

            <span
              style={{
                background:
                  'linear-gradient(118.8deg, #ED943C 27.32%, #E73636 91.18%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Fintech SaaS, UK
            </span>

            {'  '}|{'  '}Workflow automation engagement
          </p>

        </motion.div>

      </section>

 {/* TECTNOLOGY SECTION */}
      <section className="relative overflow-hidden bg-[#0B0B0B] py-16 md:py-24 xl:py-32">

{/* CENTER BLUE GLOW */}
<div
  className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[450px] w-[450px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[180px]"
  style={{ background: '#229CFF', opacity: 0.28 }}
/>

<div className="relative z-10 mx-auto w-[92%] max-w-[1300px]">

  {/* ── DESKTOP LAYOUT ── */}
  <div className="hidden lg:block">

    {/* TOP CENTER CARD */}
    <div className="flex justify-center mb-16">
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: false }}
        className="rounded-full bg-[#171717] px-6 py-4 hover:-translate-y-2 transition-all duration-500"
      >
        <div className="flex items-center gap-3">
          <div className="h-2.5 w-2.5 rounded-full bg-[#229CFF]" />
          <p className="font-google-sans-medium text-base text-white xl:text-lg">Next.js / React</p>
        </div>
      </motion.div>
    </div>

    {/* MIDDLE ROW: LEFT CARDS + CENTER CONTENT + RIGHT CARDS */}
    <div className="flex items-center justify-between gap-6">

      {/* LEFT COLUMN — 3 cards */}
      <div className="flex flex-col gap-10 min-w-[220px]">
        {[
          { title: 'Docker/Railway/AWS', dot: '#ED943C' },
          { title: 'Python / FastAPI',   dot: '#229CFF' },
          { title: 'Claude / GPT-4o/Llama', dot: '#ED943C' },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            viewport={{ once: false }}
            className="rounded-full bg-[#171717] px-6 py-4 hover:-translate-y-2 transition-all duration-500"
          >
            <div className="flex items-center gap-3">
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: item.dot }} />
              <p className="font-google-sans-medium text-base text-white xl:text-lg whitespace-nowrap">{item.title}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CENTER CONTENT */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: false }}
        className="flex-1 text-center px-4 max-w-[480px] mx-auto"
      >
        <p className="font-google-sans-medium text-sm uppercase tracking-[0.35em] text-[#229CFF] md:text-base">
          TECHNOLOGY STACK
        </p>
        <h2 className="mt-4 max-w-[500px] font-google-sans-medium text-3xl leading-[1.2] text-white md:text-3xl xl:text-4xl">
          Production-grade tooling. No wrapper products.
        </h2>
        <p className="mx-auto mt-5 max-w-[400px] font-google-sans text-sm leading-[1.8] text-white/65 md:text-lg">
          We build on the infrastructure that's actually running
          in serious engineering teams in 2026 —
          not demo-friendly tools that break at scale.
        </p>
        {/* DOTS */}
        <div className="mt-8 flex items-center justify-center gap-5">
          <div className="h-7 w-7 rounded-full bg-[#229CFF]" />
          <div className="h-7 w-7 rounded-full" style={{ background: 'linear-gradient(118.8deg, #ED943C 27.32%, #E73636 91.18%)' }} />
          <div className="h-7 w-7 rounded-full bg-[#5A47F9]" />
        </div>
      </motion.div>

      {/* RIGHT COLUMN — 3 cards */}
      <div className="flex flex-col gap-10 min-w-[220px] items-end">
        {[
          { title: 'Solidity / EVM Chains',    dot: '#ED943C' },
          { title: 'n8n / Make.com',            dot: '#229CFF' },
          { title: 'Stripe / Razorpay/Web3',    dot: '#ED943C' },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            viewport={{ once: false }}
            className="rounded-full bg-[#171717] px-6 py-4 hover:-translate-y-2 transition-all duration-500"
          >
            <div className="flex items-center gap-3">
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: item.dot }} />
              <p className="font-google-sans-medium text-base text-white xl:text-lg whitespace-nowrap">{item.title}</p>
            </div>
          </motion.div>
        ))}
      </div>

    </div>

    {/* BOTTOM CENTER CARD */}
    <div className="flex justify-center mt-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: false }}
        className="rounded-full bg-[#171717] px-6 py-4 hover:-translate-y-2 transition-all duration-500"
      >
        <div className="flex items-center gap-3">
          <div className="h-2.5 w-2.5 rounded-full bg-[#229CFF]" />
          <p className="font-google-sans-medium text-base text-white xl:text-lg">Supabase / PostgreSQL</p>
        </div>
      </motion.div>
    </div>

  </div>

  {/* ── MOBILE / TABLET LAYOUT: stacked grid ── */}
  <div className="lg:hidden">

    {/* HEADER */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7 }}
      viewport={{ once: false }}
      className="mx-auto mt-5 mb-10 max-w-[600px] text-center"
    >
      <p className="font-google-sans-medium text-sm uppercase tracking-[0.35em] text-[#229CFF] md:text-base">
        TECHNOLOGY STACK
      </p>
      <h2 className="mt-4 font-google-sans-medium text-3xl leading-[1.2] text-white md:text-4xl xl:text-2xl">
        Production-grade tooling.
        <br />No wrapper products.
      </h2>
      <p className="mx-auto mt-5 max-w-[440px] font-google-sans text-sm leading-[1.8] text-white/65 md:text-base">
        We build on the infrastructure that's actually running
        in serious engineering teams in 2026 —
        not demo-friendly tools that break at scale.
      </p>
      <div className="mt-8 flex items-center justify-center gap-5">
        <div className="h-6 w-6 rounded-full bg-[#229CFF]" />
        <div className="h-6 w-6 rounded-full" style={{ background: 'linear-gradient(118.8deg, #ED943C 27.32%, #E73636 91.18%)' }} />
        <div className="h-6 w-6 rounded-full bg-[#5A47F9]" />
      </div>
    </motion.div>

    {/* CARDS GRID */}
  <div className="grid grid-cols-2 gap-3 md:gap-4">
  {[
    { title: 'Next.js / React',          dot: '#229CFF' },
    { title: 'Docker / Railway / AWS',   dot: '#ED943C' },
    { title: 'Solidity / EVM Chains',    dot: '#ED943C' },
    { title: 'Python / FastAPI',         dot: '#229CFF' },
    { title: 'n8n / Make.com',           dot: '#229CFF' },
    { title: 'Claude / GPT-4o/Llama',   dot: '#ED943C' },
    { title: 'Stripe / Razorpay/Web3',  dot: '#ED943C' },
    { title: 'Supabase / PostgreSQL',   dot: '#229CFF' },
  ].map((item, i) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: i * 0.07 }}
      viewport={{ once: false }}
      className="rounded-full bg-[#171717] px-3 sm:px-4 md:px-6 py-3 md:py-4 hover:-translate-y-2 transition-all duration-500"
    >
      <div className="flex items-center gap-2 md:gap-3">
        <div
          className="h-2 w-2 md:h-2.5 md:w-2.5 flex-shrink-0 rounded-full"
          style={{ background: item.dot }}
        />
        <p className="font-google-sans-medium text-xs sm:text-sm md:text-base text-white truncate">
          {item.title}
        </p>
      </div>
    </motion.div>
  ))}
</div>

  </div>

</div>

</section>
    </>
  )
}

export default MeasurableOutcomes